/**
 * Product View Dialog - Display product details
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PermissionGate } from "@/components/PermissionGate";
import { Product } from "@/types/product";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Package, Tag, Ruler, Calendar, User, FileText, Droplet, Scale, AlertCircle, DollarSign, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";

interface ProductViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  onEdit?: () => void;
}

export function ProductViewDialog({ open, onOpenChange, product, onEdit }: ProductViewDialogProps) {
  const [costData, setCostData] = useState<{
    custo_por_grama: number;
    custo_por_ml: number;
    custo_por_unidade: number;
    custo_ultimo_por_grama: number;
    custo_ultimo_por_ml: number;
    custo_ultimo_por_unidade: number;
    data_ultimo_custo: string | null;
    tem_custo_recente: boolean;
  } | null>(null);
  const [isLoadingCosts, setIsLoadingCosts] = useState(false);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [updaterName, setUpdaterName] = useState<string | null>(null);
  const [isLoadingNames, setIsLoadingNames] = useState(false);

  useEffect(() => {
    if (product && open) {
      const fetchCosts = async () => {
        setIsLoadingCosts(true);
        try {
          const { data } = await supabase
            .from('vw_produto_custo_unitario')
            .select('*')
            .eq('produto_id', product.id_produto)
            .single();
          setCostData(data);
        } catch (error) {
          console.error("Erro ao buscar custos:", error);
        } finally {
          setIsLoadingCosts(false);
        }
      };
      fetchCosts();
    }
  }, [product, open]);

  useEffect(() => {
    const fetchUserName = async (userId: string): Promise<string> => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', userId)
          .single();
        
        if (error) throw error;
        return data?.name || 'Usuário não encontrado';
      } catch (error) {
        console.error('Erro ao buscar nome do usuário:', error);
        return 'Erro ao carregar nome';
      }
    };

    const loadUserNames = async () => {
      if (!product || !open) return;
      
      setIsLoadingNames(true);
      try {
        if (product.created_by) {
          const name = await fetchUserName(product.created_by);
          setCreatorName(name);
        }
        
        if (product.updated_by && product.updated_by !== product.created_by) {
          const name = await fetchUserName(product.updated_by);
          setUpdaterName(name);
        }
      } finally {
        setIsLoadingNames(false);
      }
    };
    
    loadUserNames();
  }, [product, open]);

  if (!product) return null;

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return "-";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Detalhes do Produto
          </DialogTitle>
          <DialogDescription>Informações completas do produto cadastrado</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
        <div className="px-6 space-y-6">
          {/* Informações Básicas */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Tag className="w-4 h-4" />
              INFORMAÇÕES BÁSICAS
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="col-span-2">
                <span className="text-muted-foreground">Nome:</span>
                <p className="font-medium text-base">{product.nome}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Classificação */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Ruler className="w-4 h-4" />
              CLASSIFICAÇÃO
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Categoria:</span>
                <p className="font-medium">{product.categoria?.nome || "-"}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Características */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" />
              CARACTERÍSTICAS
            </h3>
            <div className="flex flex-wrap gap-3">
              <Badge variant={product.perecivel ? "default" : "outline"} className="text-xs">
                {product.perecivel ? "✓" : "✗"} Perecível
              </Badge>
              <Badge variant={product.controla_lote ? "default" : "outline"} className="text-xs">
                {product.controla_lote ? "✓" : "✗"} Controla Lote
              </Badge>
              <Badge variant={product.controla_validade ? "default" : "outline"} className="text-xs">
                {product.controla_validade ? "✓" : "✗"} Controla Validade
              </Badge>
            </div>
          </div>

          <Separator />

          {/* Custos Unitários */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              CUSTOS UNITÁRIOS
            </h3>

            {isLoadingCosts ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : costData ? (
              <>
                {!costData.tem_custo_recente && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      ⚠️ Este produto não possui custos recentes (últimos 30 dias)
                    </AlertDescription>
                  </Alert>
                )}

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Métrica</TableHead>
                        <TableHead className="text-right">Média 30 dias</TableHead>
                        <TableHead className="text-right">Último Adicionado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costData.custo_por_grama > 0 && (
                        <TableRow>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Scale className="w-3 h-3" /> Por Grama
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            R$ {costData.custo_por_grama.toFixed(4)}
                          </TableCell>
                          <TableCell className="text-right">
                            R$ {costData.custo_ultimo_por_grama.toFixed(4)}
                          </TableCell>
                        </TableRow>
                      )}
                      {costData.custo_por_ml > 0 && (
                        <TableRow>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Droplet className="w-3 h-3" /> Por ML
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            R$ {costData.custo_por_ml.toFixed(4)}
                          </TableCell>
                          <TableCell className="text-right">
                            R$ {costData.custo_ultimo_por_ml.toFixed(4)}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Package className="w-3 h-3" /> Por Unidade
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          R$ {costData.custo_por_unidade.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          R$ {costData.custo_ultimo_por_unidade.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {costData.data_ultimo_custo && (
                  <p className="text-xs text-muted-foreground mt-2">
                    📅 Último custo adicionado em:{" "}
                    {new Date(costData.data_ultimo_custo).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                ℹ️ Nenhum custo registrado para este produto
              </p>
            )}
          </div>

          <Separator />

          {/* Observações */}
          {product.observacoes && (
            <>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  OBSERVAÇÕES
                </h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{product.observacoes}</p>
              </div>
              <Separator />
            </>
          )}


          {/* Metadados */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              METADADOS
            </h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                <span>Criado em: {formatDate(product.created_at)}</span>
              </div>
              {product.updated_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  <span>Atualizado em: {formatDate(product.updated_at)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <User className="w-3 h-3" />
                <span>
                  Criado por: {isLoadingNames ? (
                    <span className="text-muted-foreground">Carregando...</span>
                  ) : (
                    <span className="font-medium">{creatorName || 'N/A'}</span>
                  )}
                </span>
              </div>
              {product.updated_by && product.updated_by !== product.created_by && (
                <div className="flex items-center gap-2">
                  <User className="w-3 h-3" />
                  <span>
                    Atualizado por: {isLoadingNames ? (
                      <span className="text-muted-foreground">Carregando...</span>
                    ) : (
                      <span className="font-medium">{updaterName || 'N/A'}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {onEdit && (
            <PermissionGate resource="products" action="edit">
              <Button onClick={onEdit}>Editar Produto</Button>
            </PermissionGate>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
